import { Typography } from "@mui/material";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import SnackbarMessage from "components/SnackbarMessage";
import useGlobalContext from "hooks/globalData";
import { useStrictQuery } from "hooks/useStrictQuery";
import useTitle from "hooks/useTitle";
import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { OrganizationUserDetails } from "services/KnowfullyAdminClient";
import { SnackbarMessageProps } from "types/common-types";
import ProfessionalInformationAccordion from "./ProfessionalInformationAccordion";
import CredentialDetailsAccordion from "./CredentialDetailsAccordion";

const UserProfileManagementPage = () => {
  const { userId } = useParams<{ userId: string }>();
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const { organizationUserService, userDetailsUrl } = useGlobalContext();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const refreshAll = useCallback(() => {
    queryClient.invalidateQueries("getUserProfileCredentials");
    queryClient.invalidateQueries("getProfessionalInformation");
    queryClient.invalidateQueries("getAuditLog");
  }, [queryClient]);

  const userResult = useStrictQuery<OrganizationUserDetails>(
    ["getOrganizationUserDetails", userId],
    () => organizationUserService.getOrganizationUserDetails(userId!),
    { enabled: !!userId },
  );

  const user = userResult.data;
  const title = t(
    { id: "profileManagementTitle" },
    { email: user?.email ?? "" },
  );
  useTitle(title);

  if (userResult.isLoading) return <Loader />;

  return (
    <>
      <RedirectBackButton
        redirectRoute={userDetailsUrl(userId)}
        labelKey="backToListButtonLabel"
      />

      <Typography variant="h5" gutterBottom>
        {title}
      </Typography>

      <ProfessionalInformationAccordion
        userId={userId!}
        organizationId={user!.organizationId}
        setSnackbarMessage={setSnackbarMessage}
        onDataChanged={refreshAll}
      />

      <CredentialDetailsAccordion
        userId={userId!}
        organizationId={user!.organizationId}
        setSnackbarMessage={setSnackbarMessage}
        onDataChanged={refreshAll}
      />

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
};

export default UserProfileManagementPage;
